import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { companyExecutiveQueryOptions, type CompanyExecutive } from '@/queries';

import ExecutiveDetailDrawer, { type ExecutiveCompanyContext } from './ExecutiveDetailDrawer';
import { findExecutiveProfile } from './utils';

interface ExecutiveProfileLinkProps extends ExecutiveCompanyContext {
    executiveName: string;
}

const ExecutiveProfileLink: FC<ExecutiveProfileLinkProps> = ({ executiveName, logoUrl, name, symbol, website }) => {
    const { token } = useAntDesignTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedExecutive, setSelectedExecutive] = useState<CompanyExecutive>();
    const { data: executives = [] } = useQuery({
        ...companyExecutiveQueryOptions.roster(symbol ?? ''),
        enabled: Boolean(symbol),
    });
    const fallbackExecutive = useMemo<CompanyExecutive>(
        () => ({
            active: true,
            id: `company-ceo:${symbol ?? name}:${executiveName}`,
            name: executiveName,
            title: 'Chief Executive Officer',
        }),
        [executiveName, name, symbol],
    );
    const matchedExecutive = useMemo(
        () => findExecutiveProfile(executives, executiveName),
        [executiveName, executives],
    );
    const profile = matchedExecutive ?? fallbackExecutive;
    const drawerExecutives = matchedExecutive ? executives : [fallbackExecutive];
    const resolvedSelectedExecutive =
        selectedExecutive?.id === fallbackExecutive.id && matchedExecutive
            ? matchedExecutive
            : (selectedExecutive ?? profile);

    return (
        <>
            <Typography.Link
                aria-label={`Open ${executiveName} profile`}
                style={{
                    color: token.colorText,
                    textDecoration: 'underline',
                    textUnderlineOffset: '0.125rem',
                }}
                onClick={() => {
                    setSelectedExecutive(profile);
                    setDrawerOpen(true);
                }}
            >
                {executiveName}
            </Typography.Link>
            <ExecutiveDetailDrawer
                company={{ logoUrl, name, symbol, website }}
                executive={resolvedSelectedExecutive}
                executives={drawerExecutives}
                open={drawerOpen}
                onExecutiveChange={setSelectedExecutive}
                onOpenChange={setDrawerOpen}
            />
        </>
    );
};

export default ExecutiveProfileLink;
